import React, { Component, Key } from 'react';
import styles from './FilesComponent.module.scss';
import Icon from '@ant-design/icons';
import { FileExcelOutlined } from '@ant-design/icons';
import { ReactComponent as CaretDownSvg } from '../../../../resources/images/caret-down.svg';
import { ReactComponent as EditSvg } from '../../../../resources/images/edit.svg';
import { FormattedMessage, injectIntl, WrappedComponentProps, FormattedDate } from 'react-intl';
import { CustomAttributeDefinition, CustomAttribute, File } from '../../../../model/entities';
import { ColumnsType } from 'antd/lib/table';
import FileSizeComponent from '../../../../components/FileSizeComponent/FileSizeComponent';
import { TableRowSelection } from 'antd/lib/table/interface';
import { Button, Dropdown, Menu, message, Space, Tooltip } from 'antd';
import Search from 'antd/lib/input/Search';
import stringService from '../../../../services/StringService';
import UpdateCustomAttributesModal from './UpdateCustomAttributesModal/UpdateCustomAttributesModal';
import notificationService from '../../../../services/NotificationService';
import fileApi from '../../../../api/FileApi';
import tableService from '../../../../services/TableService';
import SelectTableComponent from '../../../../components/SelectTableComponent/SelectTableComponent';

class FilesComponent extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { selectedRowKeys: [], selectedFiles: [] };
    }

    /** METHODS **/

    search = (searchText: string) => {
        this.props.onSearch(searchText);
    };

    select = (selectedRowKeys: Key[], selectedRows: File[]) => {
        this.setState({ selectedRowKeys, selectedFiles: selectedRows });
    };

    unselectAll = () => {
        this.setState({ selectedRowKeys: [], selectedFiles: [] });
    };

    showCustomAttributesModal = async (customAttributesModalVisible: boolean) => {
        this.setState({ customAttributesModalVisible });
    };

    updateCustomAttributes = async (customAttributes: CustomAttribute[]) => {
        const { selectedFiles } = this.state;
        try {
            const selectedFilesWithUpdatedAttributes = selectedFiles.map((f) =>
                Object.assign({}, f, {
                    customAttributes: customAttributes.filter((ca) => ca.projectId === f.projectId),
                }),
            );
            const filesWithStatus = await fileApi.updateCustomAttributes(selectedFilesWithUpdatedAttributes);
            this.unselectAll();
            this.showCustomAttributesModal(false);

            // display message
            if (filesWithStatus.every((f) => f.status.type === 'OK')) {
                message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            } else {
                message.warning(this.props.intl.formatMessage({ id: 'status.savedWithErrors' }), 5);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    isUpdateAttributesDisabled = (): boolean => {
        return this.state.selectedFiles.length === 0;
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderToolbar()}
                {this.renderTable()}
                {this.state.customAttributesModalVisible && this.renderCustomAttributesModal()}
            </>
        );
    };

    renderToolbar = (): React.ReactElement | undefined => {
        const { intl, loading } = this.props;
        const { selectedFiles } = this.state;
        const placeholder: string = intl.formatMessage({ id: 'files.search' });
        const disabled = selectedFiles.length === 0;

        return (
            <div className="toolbar">
                <Search
                    placeholder={placeholder}
                    onSearch={this.search}
                    size="large"
                    className="search"
                    disabled={!!loading}
                />
                <Space>
                    <Tooltip title={<FormattedMessage id="files.exportSpreadSheet" />}>
                        <Button size="large" icon={<FileExcelOutlined />} disabled={true}></Button>
                    </Tooltip>
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        disabled={disabled}
                        overlay={
                            <Menu>
                                <Menu.Item
                                    key="attributes"
                                    icon={<Icon component={EditSvg} />}
                                    onClick={() => this.showCustomAttributesModal(true)}
                                    disabled={this.isUpdateAttributesDisabled()}
                                >
                                    <FormattedMessage id="files.updateCustomAttributes" />
                                </Menu.Item>
                            </Menu>
                        }
                    >
                        <Button type="primary" size="large">
                            <FormattedMessage id="button.actions" /> <Icon component={CaretDownSvg} />
                        </Button>
                    </Dropdown>
                </Space>
            </div>
        );
    };

    renderCustomAttributesModal = (): React.ReactElement | undefined => {
        const { customAttributeDefinitions } = this.props;
        const { customAttributesModalVisible } = this.state;
        return (
            <UpdateCustomAttributesModal
                visible={customAttributesModalVisible}
                customAttributeDefinitions={customAttributeDefinitions}
                onUpdate={this.updateCustomAttributes}
                onCancel={() => this.showCustomAttributesModal(false)}
            />
        );
    };

    renderCustomAttributeColumns = (): ColumnsType<File> => {
        const { customAttributeDefinitions } = this.props;

        return customAttributeDefinitions.map((cad) => ({
            title: <Tooltip title={cad.name}>{cad.name}</Tooltip>,
            key: `customAttributeDefinitions.${cad.id}`,
            className: styles.inline,
            render: (value: any, file: File) => <Tooltip title="XXX">XXX</Tooltip>,
        }));
    };

    renderTable = (): React.ReactElement | undefined => {
        const { files, loading } = this.props;
        const { selectedRowKeys, selectedFiles } = this.state;
        const columns: ColumnsType<File> = [
            {
                title: <FormattedMessage id="file.name" />,
                dataIndex: 'name',
                key: 'name',
                className: styles.inline,
                defaultSortOrder: 'ascend',
                sorter: {
                    compare: (a, b) => stringService.normalize(a.name).localeCompare(stringService.normalize(b.name)),
                },
                render: (value: string) => <Tooltip title={value}>{value}</Tooltip>,
            },
            {
                title: <FormattedMessage id="file.path" />,
                dataIndex: 'path',
                key: 'path',
                className: styles.inline,
                defaultSortOrder: 'ascend',
                sorter: {
                    compare: (a, b) => stringService.normalize(a.path).localeCompare(stringService.normalize(b.path)),
                },
                render: (value: string) => <Tooltip title={value}>{value}</Tooltip>,
            },
            {
                title: <FormattedMessage id="file.updatedAt" />,
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                align: 'center',
                width: '180px',
                sorter: (a, b) => (a.updatedAt!.isAfter(b.updatedAt) ? 1 : -1),
                render: (value: string) => {
                    return (
                        <FormattedDate
                            value={value}
                            day="2-digit"
                            month="2-digit"
                            year="numeric"
                            hour="2-digit"
                            minute="2-digit"
                        />
                    );
                },
            },
            {
                title: <FormattedMessage id="file.updatedBy" />,
                dataIndex: 'updatedBy',
                key: 'updatedBy',
                width: '200px',
                sorter: {
                    compare: (a, b) =>
                        stringService.normalize(a.updatedBy).localeCompare(stringService.normalize(b.updatedBy)),
                },
            },
            {
                title: <FormattedMessage id="file.version" />,
                dataIndex: 'version',
                key: 'version',
                align: 'right',
                width: '100px',
                sorter: (a, b) => a.version! - b.version!,
            },
            {
                title: <FormattedMessage id="file.size" />,
                dataIndex: 'size',
                key: 'size',
                align: 'right',
                width: '100px',
                sorter: (a, b) => a.size! - b.size!,
                render: (value: number) => {
                    return <FileSizeComponent value={value} />;
                },
            },
            // ...this.renderCustomAttributeColumns(),
        ];
        const rowSelection: TableRowSelection<File> = {
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: this.select,
        };

        return (
            <SelectTableComponent
                dataSource={files}
                columns={columns}
                pagination={tableService.createMemoryPagination(files)}
                rowKey="id"
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
                rowSelection={rowSelection}
                className={styles.table}
                selectedItems={selectedFiles.map((file) => file.name)}
                onUnselectAll={this.unselectAll}
            />
        );
    };

    render() {
        return this.renderContent();
    }
}
export default injectIntl(FilesComponent);

interface Props extends WrappedComponentProps {
    files: File[];
    customAttributeDefinitions: CustomAttributeDefinition[];
    loading?: boolean;
    onSearch: (searchText: string) => void;
}

interface State {
    selectedRowKeys: Key[];
    selectedFiles: File[];
    searchText?: string;
    customAttributesModalVisible?: boolean;
}
